import { Injectable } from '@angular/core';

import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Subject, Observable, BehaviorSubject, Subscription, of } from 'rxjs';
import { tap, map, timeout } from 'rxjs/operators';
import * as FileSaver from 'file-saver';

import { environment } from '@environments/environment';
import { Order } from '@classes/order/order';
import { UiService } from './ui.service';
import { Pagination } from '@classes/pagination/pagination';
import { PaginationRequest } from '@classes/pagination/pagination-request';

import * as moment from 'moment';
import { User } from '@classes/user/user';
import { Site } from '@classes/site';
import { Counter } from '@classes/order/counter';
import { InternalComment } from '@classes/order/internalComment';
import { OrderStepTemperature } from '@classes/order/orderStepTemperature';
import { AddDocumentRequest } from '@classes/document/add-document-request';
import { AddOrderDocumentRequest } from '@classes/order/add-order-document-request';
import { ExportOrdersRequest } from '@classes/order/export-orders-request';
import { Document } from '@classes/document/document';
import { FilterHelperService } from '../helpers/filter-helper.service';
@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  ordersListChanged = new Subject<Order[]>();
  selectedOrderChanged = new Subject<Order>();
  counterTriggered = new Subject<boolean>();

  paginationSubject = new BehaviorSubject<Pagination>(null);
  unassignedToTreatSubject = new BehaviorSubject<number>(0);
  assignedToTreatSubject = new BehaviorSubject<number>(0);

  dateNow: Date;

  private baseUrl: string = environment.apiUrl + 'orders';
  private orders: Order[];
  private lastQuery: string;
  private lastQueryObj: {
    name: null;
    template: null;
    status: null;
    user: null;
    client: null;
    fromStartDate: null;
    fromCreatedOn: null;
    fromModifiedOn: null;
    createdBy: null;
  };
  private getListSub: Subscription;
  private selectedOrder: Order;

  constructor(
    private http: HttpClient,
    private uiSrv: UiService,
    private _filterHelper: FilterHelperService,
  ) {}

  getList(request: PaginationRequest, isMission?: boolean): void {
    this.dateNow = new Date();
    this.uiSrv.loadingStateChanged.next(true);
    this.ordersListChanged.next([]);

    let httpParams = new HttpParams();

    if (this.getListSub) {
      this.getListSub.unsubscribe();
    }

    if (request.pageSize && request.page) {
      httpParams = httpParams
        .append('pageSize', request.pageSize.toString())
        // .append("page", request.query !== this.lastQuery ? "1" : request.page.toString());
        .append(
          'page',
          request.query !== this.lastQueryObj ? '1' : request.page.toString(),
        );
    }

    if (request.query) {
      const filters = request.query;

      if (filters.includeDeleted) {
        httpParams = httpParams.append('includeDeleted', 'true');
      }

      const filtersQuery = this.generateFiltersQuery(filters);

      httpParams = httpParams.append('Filters', filtersQuery);
    }

    if (request.sort) {
      httpParams = httpParams.append('sorts', request.sort);
    } else {
      // sort par défaut pour l'optimisation de requêtes
      httpParams = httpParams.append('sorts', 'startDate');
    }

    if (isMission) {
      httpParams = httpParams.append('isAssigned', 'true');
    } else {
      httpParams = httpParams.append('isAssigned', 'false');
    }

    // this.lastQuery = request.query;
    this.lastQueryObj = request.query;

    this.getListSub = this.http
      .get(this.baseUrl, { params: httpParams, observe: 'response' })
      .subscribe((result: HttpResponse<Order[]>) => {
        this.uiSrv.loadingStateChanged.next(false);
        const pagination: Pagination = JSON.parse(
          result.headers.get('X-Pagination'),
        );
        this.orders = result.body;
        this.ordersListChanged.next([...this.orders]);
        this.paginationSubject.next({ ...pagination });
      });
  }

  public generateFiltersQuery(filters: any): string {
    const filtersQuery = [];

    if (filters.identifier) {
      filtersQuery.push(`identifier==${this._filterHelper.escapeSpecialFilterCharacters(filters.identifier)}`);
    }
    if (filters.type) {
      if (filters.type !== 'all') {
        filtersQuery.push(`type==${this._filterHelper.escapeSpecialFilterCharacters(filters.type)}`);
      }
    }
    if (filters.status) {
      if (filters.status !== 'all') {
        filtersQuery.push(`status==${this._filterHelper.escapeSpecialFilterCharacters(filters.status)}`);
      }
    }
    if (filters.driver) {
      filtersQuery.push(`driver==${this._filterHelper.escapeSpecialFilterCharacters(filters.driver)}`);
    }
    if (filters.client) {
      filtersQuery.push(`client@=${this._filterHelper.escapeSpecialFilterCharacters(filters.client)}`);
    }
    if (filters.lot) {
      filtersQuery.push(
        `lot@=${this._filterHelper.escapeSpecialFilterCharacters(filters.lot)}`,
      );
    }
    if (filters.vehicle) {
      filtersQuery.push(
        `vehicleNumberplate==${this._filterHelper.escapeSpecialFilterCharacters(filters.vehicle)}`,
      );
    }

    if (filters.site) {
      if (typeof filters.site !== 'string') {
        filtersQuery.push(`site==${this._filterHelper.escapeSpecialFilterCharacters(filters.site.name)}`);
      } else {
        filtersQuery.push(`site@=${this._filterHelper.escapeSpecialFilterCharacters(filters.site)}`);
      }
    }
    if (filters.range?.fromStartDate) {
      filtersQuery.push(
        `startDate>=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
      );
    }
    if (filters.range?.toStartDate) {
      filtersQuery.push(
        `startDate<=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
      );
    }
    if (filters.createdBy) {
      if (typeof filters.createdBy !== 'string') {
        let createdBy = filters.createdBy.username;
        if (createdBy === 'Système') {
          filtersQuery.push(`isCreatedBySystem`);
        } else if (createdBy === 'Tous') {
        } else {
          filtersQuery.push(`createdBy==${this._filterHelper.escapeSpecialFilterCharacters(createdBy)}`);
        }
      }
    }
    if (filters.urgency) {
      filtersQuery.push(`urgency@=${this._filterHelper.escapeSpecialFilterCharacters(filters.urgency)}`);
    }
    if (filters.sender) {
      filtersQuery.push(`sender@=${this._filterHelper.escapeSpecialFilterCharacters(filters.sender)}`);
    }
    if (filters.receiver) {
      filtersQuery.push(`receiver@=${this._filterHelper.escapeSpecialFilterCharacters(filters.receiver)}`);
    }
    if (filters.transportVoucher) {
      filtersQuery.push(`transportVoucher@=${this._filterHelper.escapeSpecialFilterCharacters(filters.transportVoucher)}`);
    }

    return filtersQuery.join();
  }

  getSearchOverall(filters): void {
    const filtersQuery = [];
    let httpParams = new HttpParams();

    if (filters.driver) {
      filtersQuery.push(`driver@=${this._filterHelper.escapeSpecialFilterCharacters(filters.driver.username)}`);
    }

    if (filters.globalFilter) {
      filtersQuery.push(`globalFilter@=${this._filterHelper.escapeSpecialFilterCharacters(filters.globalFilter)}`);
    }

    if (filters.scanCode) {
      filtersQuery.push(`scanCode==${this._filterHelper.escapeSpecialFilterCharacters(filters.scanCode)}`);
    }

    if (filters.productType) {
      filtersQuery.push(`productType==${this._filterHelper.escapeSpecialFilterCharacters(filters.productType.id)}`);
    }

    if (filters.site) {
      filtersQuery.push(`site@=${this._filterHelper.escapeSpecialFilterCharacters(filters.site.name)}`);
    }

    if (filters.lot) {
      filtersQuery.push(
        `lot@=${this._filterHelper.escapeSpecialFilterCharacters(filters.lot)}`,
      );
    }

    if (filters.transportVoucher) {
      filtersQuery.push(`transportVoucher@=${this._filterHelper.escapeSpecialFilterCharacters(filters.transportVoucher)}`);
    }

    if (filters.tag) {
      filtersQuery.push(`tag@=${this._filterHelper.escapeSpecialFilterCharacters(filters.tag)}`);
    }

    if (filters.startDate) {
      filtersQuery.push(
        `startDate>=${moment(new Date(filters.startDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
      );
      filtersQuery.push(
        `startDate<=${moment(new Date(filters.startDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
      );
    }

    httpParams = httpParams.append('Filters', filtersQuery.join());

    this.getListSub = this.http
      .get(this.baseUrl, { params: httpParams, observe: 'response' })
      .subscribe((result: HttpResponse<Order[]>) => {
        this.uiSrv.loadingStateChanged.next(false);

        this.orders = result.body;
        this.ordersListChanged.next([...this.orders]);
      });
  }

  get(id: string): Observable<Order> {
    return this.http.get<Order>(`${this.baseUrl}/${id}`).pipe(
      map((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrder = orderFromApi;
        this.selectedOrderChanged.next(orderFromApi);
        return orderFromApi;
      }),
    );
  }

  getCounter(): Observable<Counter> {
    return this.http.get<Counter>(`${this.baseUrl}/counter`).pipe(
      map((counter) => {
        this.assignedToTreatSubject.next(counter.assignedToTreat);
        this.unassignedToTreatSubject.next(counter.unassignedToTreat);
        this.counterTriggered.next(true);

        return counter;
      }),
    );
  }

  getInternalComment(orderId: string): Observable<InternalComment> {
    return this.http
      .get<InternalComment>(`${this.baseUrl}/${orderId}/internal-comment`)
      .pipe(
        map((internalComment) => {
          return internalComment;
        }),
      );
  }

  getTemperatures(orderId: string): Observable<OrderStepTemperature[]> {
    return this.http.get<any>(`${this.baseUrl}/${orderId}/temperatures`).pipe(
      map((temperatures) => {
        return temperatures.steps ?? null;
      }),
    );
  }

  get selectedId(): string {
    return this.selectedOrder ? this.selectedOrder.id : null;
  }

  exportLetter(id: string): Observable<Blob> {
    this.uiSrv.loadingStateChanged.next(true);
    return this.http
      .get(`${this.baseUrl}/${id}/pdf`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  exportRoadmap(id: string): Observable<Blob> {
    this.uiSrv.loadingStateChanged.next(true);
    return this.http
      .get(`${this.baseUrl}/${id}/roadmap`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  generateAllLettersPdf(filters: any) {
    let filtersQuery = '?';

    if (filters.site) {
      filtersQuery += `siteId=${filters.site.id}`;
    }

    if (filters.range?.fromStartDate) {
      filtersQuery += `&startDate=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm')}`;
    }

    if (filters.range?.toStartDate) {
      filtersQuery += `&endDate=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm')}`;
    }

    if (filters.status) {
      if (filters.status !== 'all') {
        filtersQuery += `&status=${filters.status}`;
      }
    }

    return this.http.post(
      `${this.baseUrl}/generate-all-pdf` + filtersQuery,
      null,
    );
  }

  exportExcel(orders?: string[], includePrices?: boolean): Observable<Blob> {
    this.uiSrv.loadingStateChanged.next(true);

    let httpParams = new HttpParams();

    if (includePrices) {
      httpParams = httpParams.append('includePrices', 'true');
    }

    return this.http
      .get(`${this.baseUrl}/xlsx?ids=${orders}`, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  exportExcelAll(
    request: PaginationRequest,
    isMission?: boolean,
    includePrices?: boolean,
  ) {
    let httpParams = new HttpParams();

    if (includePrices) {
      httpParams = httpParams.append('includePrices', 'true');
    }

    if (request.sort) {
      httpParams = httpParams.append('sorts', request.sort);
    }

    if (request.query) {
      const filters = request.query;
      const filtersQuery = [];

      if (filters.identifier) {
        filtersQuery.push(`identifier==${filters.identifier}`);
      }
      if (filters.type) {
        if (filters.type !== 'all') {
          filtersQuery.push(`type==${filters.type}`);
        }
      }
      if (filters.status) {
        if (filters.status !== 'all') {
          filtersQuery.push(`status==${filters.status}`);
        }
      }
      if (filters.driver) {
        filtersQuery.push(`driver@=${filters.driver}`);
      }
      if (filters.client) {
        filtersQuery.push(`client@=${filters.client}`);
      }

      if (filters.lot) {
        filtersQuery.push(
          `lot@=${this._filterHelper.formatFilterValue(filters.lot)}`,
        );
      }

      if (filters.site) {
        if (typeof filters.site !== 'string') {
          filtersQuery.push(`site==${filters.site.name}`);
        } else {
          filtersQuery.push(`site@=${filters.site}`);
        }
      }

      if (filters.range?.fromStartDate) {
        filtersQuery.push(
          `startDate>=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
        );
      }
      if (filters.range?.toStartDate) {
        filtersQuery.push(
          `startDate<=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
        );
      }

      if (filters.createdBy) {
        filtersQuery.push(`createdBy@=${filters.createdBy}`);
      }
      if (filters.urgency) {
        filtersQuery.push(`urgency@=${filters.urgency}`);
      }
      if (filters.sender) {
        filtersQuery.push(`sender@=${filters.sender}`);
      }
      if (filters.receiver) {
        filtersQuery.push(`receiver@=${filters.receiver}`);
      }

      httpParams = httpParams.append('Filters', filtersQuery.join());
    }

    if (isMission) {
      httpParams = httpParams.append('isAssigned', 'true');
    } else {
      httpParams = httpParams.append('isAssigned', 'false');
    }

    this.uiSrv.loadingStateChanged.next(true);
    return this.http
      .get(`${this.baseUrl}/xlsx`, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => this.saveFile(res)));
  }

  put(order: Order): Observable<Order> {
    return this.http.put<Order>(this.baseUrl, order).pipe(
      tap((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrderChanged.next(orderFromApi);
      }),
    );
  }

  changeTimeSpent(orderId: string, newTime: number): Observable<Order> {
    let object = {
      timeSpent: newTime,
    };
    return this.http
      .put<Order>(`${this.baseUrl}/${orderId}/time-spent`, object)
      .pipe(
        tap((orderFromApi) => {
          this.orders.map((order) => {
            if (order.id === orderId) {
              order.timeSpent = newTime;
            }
          });
          this.ordersListChanged.next([...this.orders]);
        }),
      );
  }

  modifySite(order: Order, newSite: Site): Observable<Order> {
    return this.http
      .put<Order>(`${this.baseUrl}/${order.id}/site`, newSite)
      .pipe(
        tap((orderFromApi) => {
          orderFromApi = Order.fromObject(orderFromApi);
          this.selectedOrderChanged.next(orderFromApi);
        }),
      );
  }

  changeStatus(orderId: string, form: any): Observable<Order> {
    return this.http.put<Order>(`${this.baseUrl}/${orderId}/status`, form).pipe(
      tap((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrderChanged.next(orderFromApi);
        this.uiSrv.showSnackbar('orders.statusChanged', true);

        this.getCounter().subscribe();
      }),
    );
  }

  post(mission: Order): Observable<Order> {
    return this.http.post<Order>(this.baseUrl, mission).pipe(
      tap(() => {
        this.getCounter().subscribe();
      }),
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        this.orders.map((order) => {
          if (order.id === id) {
            order.isDeleted = true;
          }
        });
        this.ordersListChanged.next([...this.orders]);

        this.getCounter().subscribe();
      }),
    );
  }

  public unassignDriver({ id }: Order): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}/driver`);
  }

  assignDriver(idOrder: string, driver: User, com: string): any {
    const assignedDriver = {
      driverId: driver.id,
      comment: com,
    };
    return this.http
      .put<any>(`${this.baseUrl}/${idOrder}/driver`, assignedDriver)
      .pipe(
        tap(() => {
          this.orders = this.orders.filter((m) => m.id !== idOrder);
          this.ordersListChanged.next([...this.orders]);

          this.getCounter().subscribe();
        }),
      );
  }

  private saveFile(res: HttpResponse<Blob>): Blob {
    FileSaver.saveAs(res.body, res.headers.get('filename'));
    this.uiSrv.loadingStateChanged.next(false);
    return res.body;
  }

  modifyTransportVoucher(id: string, object): Observable<Order> {
    return this.http
      .put<Order>(`${this.baseUrl}/${id}/transport-voucher`, object)
      .pipe(
        tap((orderFromApi) => {
          orderFromApi = Order.fromObject(orderFromApi);
          this.selectedOrderChanged.next(orderFromApi);
        }),
      );
  }

  sendPdfMail(orderId: string, email: string): Observable<any> {
    let emailToSend = {
      email: email,
    };
    return this.http.post<any>(
      `${this.baseUrl}/${orderId}/pdf/send`,
      emailToSend,
    );
  }

  patch(orderId: string, newInfos): Observable<Order> {
    return this.http.patch<Order>(`${this.baseUrl}/${orderId}`, newInfos).pipe(
      tap((orderFromApi) => {
        orderFromApi = Order.fromObject(orderFromApi);
        this.selectedOrderChanged.next(orderFromApi);
        this.uiSrv.showSnackbar(
          'Infos de commande modifiée avec succès',
          false,
        );
      }),
    );
  }

  patchInternalComment(
    orderId: string,
    internalComment: InternalComment,
  ): Observable<InternalComment> {
    return this.http
      .patch<InternalComment>(
        `${this.baseUrl}/${orderId}/internal-comment`,
        internalComment,
      )
      .pipe(
        tap(() => {
          this.uiSrv.showSnackbar(
            'Commentaire interne modifié avec succès',
            false,
          );
        }),
      );
  }

  public getDocuments$(orderId: string): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.baseUrl}/${orderId}/documents`);
  }

  public postDocument$(
    orderId: string,
    addDocumentRequest: AddOrderDocumentRequest,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/${orderId}/document`,
      addDocumentRequest,
    );
  }

  public downloadDocuments$(orderId: string): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/${orderId}/documents/zip`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((response) => this.saveFile(response)));
  }

  public export$(
    exportOrdersRequest: ExportOrdersRequest,
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}/export`, exportOrdersRequest, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  public patchPrice$(orderId: string, price: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/${orderId}/price`, { price });
  }
}
